import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, TrendingUp, TrendingDown, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhysicalProgress = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProgressData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchProgressData = async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/progress`);
      setProgressData(response.data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = progressData?.disciplines?.map(d => ({
    name: d.discipline.length > 10 ? d.discipline.substring(0, 10) + '...' : d.discipline,
    fullName: d.discipline,
    planned: d.planned,
    actual: d.actual,
    variance: d.variance,
    weight: d.weight
  })) || [];

  if (loading && !progressData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="physical-progress-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('progress.title')}</h1>
            <p className="text-xs text-gray-500">{t('progress.subtitle')}</p>
          </div>
        </div>
        
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('common.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 25)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Overall Progress Card */}
        {progressData?.overall && (
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium opacity-80 mb-2">{t('progress.overallProgress')}</h3>
              <div className="flex items-end justify-between">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">{progressData.overall.overall_actual}%</span>
                    <span className="text-sm opacity-70">/ {progressData.overall.overall_planned}%</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {progressData.overall.variance >= 0 ? (
                      <TrendingUp size={14} className="text-green-300" />
                    ) : (
                      <TrendingDown size={14} className="text-red-300" />
                    )}
                    <span className={`text-sm ${progressData.overall.variance >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                      {progressData.overall.variance >= 0 ? '+' : ''}{progressData.overall.variance}% {t('progress.variance')}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs opacity-70">SPI</div>
                  <div className="text-xl font-bold">{progressData.overall.spi}</div>
                </div>
              </div>
              <div className="w-full h-2 bg-white/20 rounded-full mt-3 overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all"
                  style={{ width: `${progressData.overall.overall_actual}%` }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('progress.byDiscipline')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 10 }}>
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 9 }} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 shadow-lg rounded border text-xs">
                            <p className="font-semibold">{data.fullName}</p>
                            <p className="text-blue-600">{t('common.planned')}: {data.planned}%</p>
                            <p className="text-green-600">{t('common.actual')}: {data.actual}%</p>
                            <p className={data.variance >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {t('common.variance')}: {data.variance >= 0 ? '+' : ''}{data.variance}%
                            </p>
                            <p className="text-gray-500">{t('progress.weight')}: {data.weight}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="planned" name={t('common.planned')} fill="#93c5fd" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="actual" name={t('common.actual')} radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.variance >= 0 ? '#22c55e' : '#ef4444'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Discipline Details */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('progress.disciplineDetails')}</h3>
          {progressData?.disciplines?.map((discipline, index) => (
            <Card key={index}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-gray-900">{discipline.discipline}</span>
                  <span className="text-xs text-gray-500">{t('progress.weight')}: {discipline.weight}%</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-blue-50 rounded p-2">
                    <p className="text-xs text-gray-500">Planned</p>
                    <p className="text-sm font-bold text-blue-600">{discipline.planned}%</p>
                  </div>
                  <div className="bg-green-50 rounded p-2">
                    <p className="text-xs text-gray-500">Actual</p>
                    <p className="text-sm font-bold text-green-600">{discipline.actual}%</p>
                  </div>
                  <div className={`rounded p-2 ${discipline.variance >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className="text-xs text-gray-500">Variance</p>
                    <p className={`text-sm font-bold ${discipline.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {discipline.variance >= 0 ? '+' : ''}{discipline.variance}%
                    </p>
                  </div>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${discipline.variance >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${discipline.actual}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PhysicalProgress;
